(() => {
  if (customElements.get('vinut-rating-blurb')) return;

  class RatingBlurb extends HTMLElement {
    constructor() {
      super();
      this.schedule = this.schedule.bind(this);
      this.mount = this.mount.bind(this);
      this.activate = this.activate.bind(this);
    }

    connectedCallback() {
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', this.mount, { once: true });
      else this.mount();
    }

    mount() {
      if (!this.isConnected || this.mounted) return;
      this.row = this.firstElementChild;
      this.trigger = this.querySelector('.reviews-count');
      if (!this.row || !this.trigger) return;
      this.mounted = true;
      this.scale = parseFloat(this.style.getPropertyValue('--rating-fit-scale')) || 1;
      this.trigger.addEventListener('click', this.activate);
      this.trigger.addEventListener('keydown', this.activate);
      if (typeof ResizeObserver === 'function') {
        // Layout has already been calculated for these entries; never force it during fitting.
        this.observer = new ResizeObserver(entries => {
          for (const entry of entries) {
            if (entry.target === this) this.available = entry.contentRect.width;
            else this.rendered = entry.contentRect.width;
          }
          this.schedule();
        });
        this.observer.observe(this);
        this.observer.observe(this.row);
      } else {
        window.addEventListener('resize', this.schedule, { passive: true });
        document.fonts?.addEventListener('loadingdone', this.schedule);
        document.fonts?.ready.then(this.schedule);
        this.mutations = new MutationObserver(this.schedule);
        this.mutations.observe(this.row, { childList: true, subtree: true, characterData: true });
        this.schedule();
      }
    }

    schedule() {
      if (!this.isConnected || !this.mounted || this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.fit(); });
    }

    fit() {
      const available = this.observer ? this.available : this.clientWidth;
      const rendered = this.observer ? this.rendered : this.row.getBoundingClientRect().width;
      if (!available || !rendered) return;
      const natural = rendered / this.scale;
      const scale = natural <= available ? 1 : Math.floor((available - 0.25) / natural * 1000000) / 1000000;
      // A subpixel tolerance prevents rounding-only resize feedback while retaining the largest fitting font.
      const settled = scale < 1 && rendered <= available && available - rendered < 0.5;
      if (scale > 0 && scale !== this.scale && !settled) {
        this.scale = scale;
        this.style.setProperty('--rating-fit-scale', String(scale));
        if (!this.observer) this.schedule();
      }
      if (!this.hasAttribute('data-fit-ready')) this.setAttribute('data-fit-ready', '');
    }

    activate(event) {
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      const target = document.querySelector(this.trigger.dataset.scrollTarget || '#judgeme_product_reviews') ||
        document.querySelector('#shopify-block-ATHdXTjJEM2dHTXVWa__judge_me_reviews_review_widget_LY7YDp');
      if (!target) return;
      const manual = this.trigger.dataset.scrollOffset;
      let offset = 0;
      if (manual && manual.trim()) offset = parseInt(manual, 10) || 0;
      else {
        const selectors = 'header[style*="position: sticky"],.shopify-section-header-sticky,.shopify-section-header,header.site-header,header.header-wrapper,#shopify-section-header,header[style*="position:fixed"]';
        document.querySelectorAll(selectors).forEach(header => {
          const style = getComputedStyle(header);
          if (['fixed', 'sticky'].includes(style.position) && parseInt(style.top || '0', 10) === 0) {
            offset = Math.max(offset, header.getBoundingClientRect().height);
          }
        });
      }
      window.scrollTo({ top: target.getBoundingClientRect().top + scrollY - Math.max(0, offset),
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.mount);
      if (!this.mounted) return;
      this.mounted = false;
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.observer?.disconnect();
      this.observer = null;
      this.available = this.rendered = 0;
      this.mutations?.disconnect();
      window.removeEventListener('resize', this.schedule);
      document.fonts?.removeEventListener('loadingdone', this.schedule);
      this.trigger.removeEventListener('click', this.activate);
      this.trigger.removeEventListener('keydown', this.activate);
    }
  }
  customElements.define('vinut-rating-blurb', RatingBlurb);
})();
